import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  MessageCircle, Users, Unlock, TrendingUp,
  RefreshCw, ChevronRight, Sparkles, BarChart3, Clock
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import { format } from "date-fns";
import AdminLayout from "@/components/AdminLayout";

const COLORS = ['#1C3A7A', '#FAB710', '#22C55E', '#EF4444', '#8B5CF6'];

const AdminMessaging = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [promptStats, setPromptStats] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [recentConvos, setRecentConvos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, trendsRes, promptRes, engageRes, convosRes] = await Promise.all([
        api.getAdminMessagingStats(),
        api.getAdminMessagingTrends(14),
        api.getAdminPromptStats(),
        api.getAdminMessagingEngagement(),
        api.getAdminRecentConversations(10)
      ]);
      setStats(statsRes.data);
      setTrends(trendsRes.data);
      setPromptStats(promptRes.data);
      setEngagement(engageRes.data);
      setRecentConvos(convosRes.data);
    } catch (error) {
      toast.error("Failed to load messaging analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  if (loading) {
    return (
      <AdminLayout>
      <div className="bg-gray-50 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
      </AdminLayout>
    );
  }

  const messageTypeData = stats ? [
    { name: "Guided", value: stats.message_breakdown.guided, color: "#1C3A7A" },
    { name: "Free-form", value: stats.message_breakdown.freeform, color: "#FAB710" }
  ] : [];

  return (
    <AdminLayout>
    <div className="bg-gray-50" data-testid="admin-messaging-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] text-white px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Guided Messaging</h1>
              <p className="text-white/70">Connection & message analytics</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <MessageCircle className="w-8 h-8" />
            Messaging Analytics
          </h1>
          <p className="text-white/80 mt-1">
            Guided conversations and engagement metrics
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Conversations"
            value={stats?.overview.total_conversations || 0}
            icon={MessageCircle}
            color="bg-[#1C3A7A]"
          />
          <StatCard
            title="Unlocked Chats"
            value={stats?.overview.unlocked_conversations || 0}
            subtext={`${stats?.overview.unlock_rate || 0}% unlock rate`}
            icon={Unlock}
            color="bg-green-500"
          />
          <StatCard
            title="Total Messages"
            value={stats?.overview.total_messages || 0}
            subtext={`${stats?.overview.avg_messages_per_conversation || 0} avg/convo`}
            icon={TrendingUp}
            color="bg-[#FAB710]"
          />
          <StatCard
            title="Active (7d)"
            value={stats?.overview.active_conversations_7d || 0}
            icon={Users}
            color="bg-purple-500"
          />
        </div>

        {/* Today's Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1C3A7A]" />
            Today&apos;s Activity
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#1C3A7A]">{stats?.today.new_conversations || 0}</p>
              <p className="text-sm text-gray-600">New Conversations</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#FAB710]">{stats?.today.messages_sent || 0}</p>
              <p className="text-sm text-gray-600">Messages Sent</p>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Message Trends */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Message Trends (14 days)</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends?.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(d) => format(new Date(d), "MM/dd")}
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    labelFormatter={(d) => format(new Date(d), "MMM dd, yyyy")}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="messages" 
                    stroke="#1C3A7A" 
                    strokeWidth={2}
                    name="Messages"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="new_conversations" 
                    stroke="#FAB710" 
                    strokeWidth={2}
                    name="New Convos"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="unlocks" 
                    stroke="#22C55E" 
                    strokeWidth={2}
                    name="Unlocks"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Message Types Pie */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Message Types</h2>
            <div className="h-64 flex items-center justify-center">
              {messageTypeData.length > 0 && messageTypeData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={messageTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {messageTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No message data yet</p>
                </div>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#1C3A7A]"></div>
                <span className="text-sm text-gray-600">Guided ({stats?.message_breakdown.guided || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FAB710]"></div>
                <span className="text-sm text-gray-600">Free-form ({stats?.message_breakdown.freeform || 0})</span>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#1C3A7A]" />
            Engagement Metrics
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">User Engagement</h3>
              <div className="space-y-3">
                <ProgressBar 
                  label="Users with Messages" 
                  value={engagement?.user_engagement.users_with_messages || 0}
                  total={engagement?.user_engagement.total_users || 1}
                  percentage={engagement?.user_engagement.messaging_rate || 0}
                />
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-[#1C3A7A]">
                    {engagement?.user_engagement.users_with_mutual_matches || 0}
                  </span> users have mutual matches
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Conversation Engagement</h3>
              <div className="space-y-3">
                <ProgressBar 
                  label="Conversations with Replies" 
                  value={engagement?.conversation_engagement.conversations_with_replies || 0}
                  total={engagement?.conversation_engagement.total_conversations || 1}
                  percentage={engagement?.conversation_engagement.response_rate || 0}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Top Prompts */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FAB710]" />
            Most Used Conversation Starters
          </h2>
          {promptStats?.most_used_prompts?.length > 0 ? (
            <div className="space-y-3">
              {promptStats.most_used_prompts.slice(0, 10).map((prompt, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                >
                  <span className="w-6 h-6 bg-[#1C3A7A] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <p className="flex-1 text-sm text-gray-700 truncate">{prompt.prompt}</p>
                  <span className="text-sm font-semibold text-[#FAB710]">
                    {prompt.usage_count} uses
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No prompt data yet</p>
          )}
        </div>

        {/* Recent Conversations */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Conversations</h2>
          {recentConvos?.conversations?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 font-medium">Users</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Messages</th>
                    <th className="pb-3 font-medium">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {recentConvos.conversations.map((convo, idx) => (
                    <tr key={idx} className="border-b last:border-b-0">
                      <td className="py-3">
                        <span className="font-medium text-gray-900">{convo.user1.name}</span>
                        <span className="text-gray-400 mx-2">&</span>
                        <span className="font-medium text-gray-900">{convo.user2.name}</span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          convo.free_chat_unlocked 
                            ? "bg-green-100 text-green-700" 
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {convo.free_chat_unlocked ? "Unlocked" : "Guided"}
                        </span>
                      </td>
                      <td className="py-3 text-gray-600">{convo.message_count}</td>
                      <td className="py-3 text-gray-500 text-sm">
                        {convo.last_message_at 
                          ? format(new Date(convo.last_message_at), "MMM dd, HH:mm")
                          : "N/A"
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No conversations yet</p>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtext, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{title}</p>
        {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

// Progress Bar Component
const ProgressBar = ({ label, value, total, percentage }) => (
  <div>
    <div className="flex justify-between text-sm mb-1">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">{value}/{total} ({percentage}%)</span>
    </div>
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div 
        className="h-full bg-[#1C3A7A] rounded-full transition-all"
        style={{ width: `${percentage}%` }}
      />
    </div>
  </div>
);

export default AdminMessaging;
